import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Download, AlertTriangle, Clock, CheckCircle2, PenLine, Users } from "lucide-react";
import { downloadBlob } from "@/lib/download";
import { toast } from "sonner";

const STATUS = {
  overdue: ["Overdue", "bg-red-100 text-red-800", AlertTriangle],
  due_soon: ["Due soon", "bg-amber-100 text-amber-800", Clock],
  ok: ["Up to date", "bg-emerald-100 text-emerald-800", CheckCircle2],
  not_set: ["No review date", "bg-muted text-muted-foreground", Clock],
};

export default function MyPolicies() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("all");
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get("/me/policies").then((r) => setData(r.data)).catch((e) => logError("my-policies", e)); }, []);

  const list = useMemo(() => (data?.policies || []).filter((p) => (sector === "all" || p.sector === sector) && p.title.toLowerCase().includes(q.toLowerCase())), [data, q, sector]);

  const exportPack = async (s, format) => {
    setBusy(`${s}-${format}`);
    try { const res = await api.get(`/me/policy-packs/${s}/export`, { params: { format }, responseType: "blob" }); downloadBlob(res); }
    catch { toast.error("Export failed"); } finally { setBusy(null); }
  };

  if (!data) return <div className="max-w-6xl mx-auto px-4 py-14 text-sm text-muted-foreground">Loading your policies…</div>;
  if (!data.packs.length) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="my-policies-empty">
        <FileText className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
        <h1 className="font-display text-2xl font-bold mb-2">No policy packs yet</h1>
        <p className="text-muted-foreground mb-6">Buy a policy pack and every policy in it becomes editable here — your service name filled in, sign-off tracking and review reminders included.</p>
        <Button asChild><Link to="/resources" data-testid="my-policies-store-link">Browse policy packs</Link></Button>
      </div>
    );
  }
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="my-policies">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="overline text-accent">My Policies</span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">Your policies &amp; procedures</h1>
          <p className="text-muted-foreground mt-2 text-sm">Open any policy to customise it for your service, record staff sign-off and set its review date. Export individual policies or your whole pack as Word or PDF.</p>
        </div>
        <div className="flex flex-wrap gap-3 text-sm" data-testid="my-policies-summary">
          <Link to="/policies/signoffs" className="rounded-lg border border-border px-3 py-2 hover:bg-muted/50 inline-flex items-center gap-1.5" data-testid="my-policies-signoff-matrix-link"><Users className="w-4 h-4" /> Staff sign-off matrix</Link>
          <span className="rounded-lg border border-border px-3 py-2"><b data-testid="my-policies-overdue">{data.overdue}</b> overdue</span>
          <span className="rounded-lg border border-border px-3 py-2"><b data-testid="my-policies-due-soon">{data.due_soon}</b> due in 30 days</span>
          {data.master_updates > 0 && <span className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-amber-900"><b data-testid="my-policies-master-updates">{data.master_updates}</b> master update{data.master_updates > 1 ? "s" : ""} to review</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5 items-center">
        <Input placeholder="Search policies…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="my-policies-search" />
        <select value={sector} onChange={(e) => setSector(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="my-policies-sector-filter">
          <option value="all">All sectors</option>
          {data.packs.map((p) => <option key={p.sector} value={p.sector}>{p.label}</option>)}
        </select>
        {data.packs.filter((p) => sector === "all" || p.sector === sector).map((p) => (
          <span key={p.sector} className="flex gap-1 ml-auto">
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => exportPack(p.sector, "docx")} data-testid={`export-pack-docx-${p.sector}`}><Download className="w-3.5 h-3.5 mr-1" /> {data.packs.length > 1 && sector === "all" ? `${p.label.split("—")[1]?.trim() || p.label} ` : ""}Word pack</Button>
            <Button size="sm" variant="outline" disabled={!!busy} onClick={() => exportPack(p.sector, "pdf")} data-testid={`export-pack-pdf-${p.sector}`}><Download className="w-3.5 h-3.5 mr-1" /> PDF pack</Button>
          </span>
        ))}
      </div>
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card" data-testid="my-policies-list">
        {list.map((p) => {
          const [label, cls, Icon] = STATUS[p.status];
          return (
            <li key={`${p.sector}-${p.title}`} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`policy-row-${p.sector}-${p.title}`}>
              <div className="min-w-0 flex-1">
                <Link to={`/policies/${p.sector}/${encodeURIComponent(p.title)}`} className="font-medium hover:underline" data-testid={`policy-open-${p.sector}-${p.title}`}>{p.title}</Link>
                <p className="text-xs text-muted-foreground">{p.sector_label}{p.customised && <> · <PenLine className="inline w-3 h-3" /> customised</>}{p.signoffs > 0 && ` · ${p.signoffs} sign-off${p.signoffs > 1 ? "s" : ""}`}</p>
              </div>
              {!p.published && <Badge variant="secondary">Coming soon</Badge>}
              {p.master_updated_at && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`policy-master-updated-${p.sector}-${p.title}`}>Master updated</Badge>}
              {p.review_due && <span className="text-xs text-muted-foreground">Review {new Date(p.review_due).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>}
              <Badge className={`${cls} hover:${cls}`} data-testid={`policy-status-${p.sector}-${p.title}`}><Icon className="w-3 h-3 mr-1" />{label}</Badge>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
